import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import services from '../services';
import withMessage from './withMessage';
import withResponseHandling from './withResponseHandling';
import { OUTPUT } from '../enums';
import { validators } from 'investira.sdk';
import { acReportsEnquadramentosChanged } from '../store/actions';

const PAGE_SIZE = 25;

const withEnquadramentos = Component => {
  function WrapComponent(props) {
    const storeDispatch = useDispatch();

    const reportsUpdatedAt = useSelector(state => state.background.reports.updatedAt);

    const entidadeId = useSelector(state => state.entidade.current.default_entidade_id);

    const assinatura = useSelector(state => {
      return (
        state.entidade.data.find(xEntidade => xEntidade.entidade_id === entidadeId)?.config
          ?.assinatura ?? false
      );
    });

    const { output } = useParams();

    const [enquadramentos, setEnquadramentos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [rowSelection, setRowSelection] = useState({});
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    });
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    function formatColumnFilters(pColumnFilters = []) {
      const xFilters = {};

      pColumnFilters.forEach(xItem => {
        xFilters[xItem.id] = xItem.value;
      });

      return xFilters;
    }

    function formatSorting(pSorting = []) {
      if (validators.isEmpty(pSorting)) {
        return 'log_id DESC, data_posicao';
      }

      const xSorts = {};

      pSorting.forEach(xItem => {
        xSorts[xItem.id] = xItem.desc;
      });

      let xStrSort = ``;

      for (const [key, value] of Object.entries(xSorts)) {
        const xOrder = value === true ? 'DESC' : 'ASC';

        xStrSort = xStrSort + key + ' ' + xOrder;
      }

      return `${xStrSort}, data_posicao`;
    }

    const readEntidadeEnquadramentos = useCallback(
      (pParams = {}) => {
        if (!enquadramentos.length) {
          setIsLoading(true);
        } else {
          setIsRefetching(true);
        }

        const xParams = {
          size: PAGE_SIZE,
          page: 1,
          ...pParams
        };

        services.enquadramentos.list(
          xParams,
          rRes => {
            const xEnquadramentos = Object.values(rRes.data);
            setEnquadramentos(xEnquadramentos);
            setRowCount(rRes.pages.total_items);

            storeDispatch(acReportsEnquadramentosChanged(xEnquadramentos));
          },
          rErr => {
            props.responseErrorHandling(rErr);
          },
          () => {
            setIsLoading(false);
            setIsRefetching(false);
          }
        );
      },
      [enquadramentos.length, output, props]
    );

    const deleteEnquadramentos = (pId, onFinish) => {
      services.enquadramentos.remove(
        pId,
        rRes => {
          props.onMessageSuccess('Enquadramento excluído com sucesso');
          readEntidadeEnquadramentos();
          setRowSelection({});
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          onFinish && onFinish();
        }
      );
    };

    const bulkDeleteEnquadramentos = pRows => {
      const xEnquadramentoId = pRows.map(xRow => {
        const { enquadramento_id } = xRow.original;
        return enquadramento_id;
      });

      xEnquadramentoId.forEach(xId => {
        deleteEnquadramentos(xId);
      });
    };

    useEffect(() => {
      const xPage = pagination.pageIndex + 1;

      //const xSort = formatSorting(sorting);

      const xFilters = formatColumnFilters(columnFilters);

      readEntidadeEnquadramentos({
        page: xPage,
        size: pagination.pageSize,
        ...xFilters
        //sort: xSort,
      });
    }, [
      readEntidadeEnquadramentos,
      pagination.pageIndex,
      pagination.pageSize,
      reportsUpdatedAt,
      entidadeId,
      columnFilters,
      sorting,
      globalFilter
    ]);

    const xProps = {
      enquadramentos,
      assinatura,
      isLoading,
      isRefetching,
      rowCount,
      pagination,
      sorting,
      columnFilters,
      rowSelection,
      setPagination,
      setRowSelection,
      setColumnFilters,
      setSorting,
      setGlobalFilter,
      readEntidadeEnquadramentos,
      deleteEnquadramentos,
      bulkDeleteEnquadramentos
    };

    return <Component {...props} {...xProps} />;
  }

  return withMessage(withResponseHandling(WrapComponent));
};

export default withEnquadramentos;
